import React, { useState } from 'react';
import { Alert, Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import moment from 'moment';
import EChartBase from '../../../components/charts/EChartBase';
import { useRiskHistory } from '../hooks/useRiskHistory';
import { sectionCaptionSx, sectionTitleSx, surfaceSx } from './dashboardStyles';
import { TrendsSkeleton } from './RiskFrameworkSkeleton';
import {
  ChartCard,
  COLORS,
  FIRMWIDE,
  formatVar,
  Legend,
  MODE_EXPOSURE,
  MODE_PNL,
  RANGES,
  STRATEGY_ORDER,
} from './StrategyRiskAnalyticsDialog';

const ROWS = [FIRMWIDE, ...STRATEGY_ORDER];
const toggleSx = { px: 1.5, py: 0.25, fontSize: '12px', fontWeight: 700 };

// Tiny inline trend line for a rail row; no axes, just the shape of the series.
const Sparkline = ({ values, color }) => {
  const nums = values.filter((v) => v != null && Number.isFinite(v));
  if (nums.length < 2) return <Box sx={{ width: 64, height: 22 }} />;
  const min = Math.min(...nums);
  const span = Math.max(...nums) - min || 1;
  const step = 64 / (nums.length - 1);
  const pts = nums.map((v, i) => `${(i * step).toFixed(1)},${(20 - ((v - min) / span) * 18).toFixed(1)}`).join(' ');
  return (
    <svg width="64" height="22" viewBox="0 0 64 22" aria-hidden="true">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
};

const RailRow = ({ name, points, mode, selected, onSelect }) => {
  const latest = points[points.length - 1];
  const trend = points.map((p) => (mode === MODE_PNL ? p.net_pnl : (p.lmv || 0) + (p.smv || 0)));
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={() => onSelect(name)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onSelect(name))}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 1.5,
        py: 1,
        cursor: 'pointer',
        borderLeft: `3px solid ${selected ? '#1e3a5f' : 'transparent'}`,
        backgroundColor: selected ? '#eef2f7' : 'transparent',
        borderBottom: '1px solid #f1f5f9',
        '&:hover': { backgroundColor: selected ? '#eef2f7' : '#f8fafc' },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '11px', fontWeight: 700, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {name === FIRMWIDE ? 'Firmwide' : name}
        </Typography>
        <Typography sx={{ fontSize: '11px', fontWeight: 500, color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
          VaR 95% {latest ? formatVar(latest.var_95) : '—'}
        </Typography>
      </Box>
      <Sparkline values={trend} color={selected ? '#1e3a5f' : COLORS.axis} />
    </Box>
  );
};

// Inline master-detail: strategy rail on the left, the selected strategy's chart in focus on the right.
// `selected` / `onSelect` are controlled by the page so strategy bubbles can focus a chart here.
export default function StrategyRiskTrendsPanel({ asOfDate, selected = FIRMWIDE, onSelect }) {
  const [rangeKey, setRangeKey] = useState('1M');
  const [mode, setMode] = useState(MODE_PNL);
  const months = RANGES.find((r) => r.key === rangeKey).months;
  const endDate = asOfDate ? moment(asOfDate).format('YYYY-MM-DD') : null;
  const startDate = endDate ? moment(endDate).subtract(months, 'months').format('YYYY-MM-DD') : null;

  const { data, isLoading, isError } = useRiskHistory(startDate, endDate, true);
  const series = data?.series || {};

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
        <Box>
          <Typography sx={sectionTitleSx}>Risk Trends</Typography>
          <Typography sx={sectionCaptionSx}>
            {mode === MODE_PNL ? 'Daily net P&L' : 'Daily LMV / SMV'} vs VaR 95% / 99%{endDate ? ` � through ${endDate}` : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ToggleButtonGroup value={mode} exclusive size="small" onChange={(e, next) => next && setMode(next)}>
            <ToggleButton value={MODE_PNL} sx={toggleSx}>P&amp;L</ToggleButton>
            <ToggleButton value={MODE_EXPOSURE} sx={toggleSx}>Exposures</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup value={rangeKey} exclusive size="small" onChange={(e, next) => next && setRangeKey(next)}>
            {RANGES.map((r) => (
              <ToggleButton key={r.key} value={r.key} sx={toggleSx}>{r.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>
      <Box sx={{ mb: 1.5 }}>
        <Legend mode={mode} />
      </Box>

      {isError && <Alert severity="error" sx={{ mb: 2 }}>Could not load risk history.</Alert>}
      {data && !data.var_available && (
        <Alert severity="warning" sx={{ mb: 2 }}>VaR history is unavailable (Risk Monitor could not be reached); showing exposures only.</Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(240px, 300px) 1fr' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Box sx={{ ...surfaceSx, overflow: 'hidden' }}>
          {ROWS.map((name) => (
            <RailRow key={name} name={name} points={series[name] || []} mode={mode} selected={name === selected} onSelect={onSelect} />
          ))}
        </Box>
        {isLoading ? (
          <TrendsSkeleton height={340} />
        ) : (
          <ChartCard key={selected} title={selected} points={series[selected] || []} mode={mode} hero />
        )}
      </Box>
    </Box>
  );
}
